import { Controller } from '@hotwired/stimulus'
import { turboRequest } from '../../../helpers/turbo_helper'
import { GRID_EVENTS } from './grid_events'

export default class extends Controller {
  static targets = ['unitInput', 'deletedText']
  static values = {
    uploadId: String,
    serviceDeliveredOn: String,
    customerId: Number,
    clientCareRelationshipId: Number,
    employeeId: Number,
    programId: Number,
    serviceFundingSourceId: Number,
    providerId: Number,
    locationProviderId: Number,
    serviceDefinitionId: Number,
    serviceRecordId: Number,
    rowNodeId: String,
    columnField: String,
    originalUnitCount: Number,
    valuesUrl: String
  }

  updateUnitCount = async event => {
    const input = event.target
    const unitCount = parseFloat(input.value) || 0
    const item = this.buildItem(unitCount)

    const success = await this.createTempServiceRecord(item)
    if (success) {
      input.classList.add('edited-cell')
      this.updateGridCell(this.calculateTotal())
    }
  }

  toggleDeletion = event => {
    event.preventDefault()

    if (this.element.classList.contains('marked-for-deletion')) {
      this.restore()
    } else {
      this.delete()
    }
  }

  delete = async () => {
    const input = this.unitInputTarget
    const item = this.buildItem(0, input)

    const success = await this.createTempServiceRecord(item)

    if (success) {
      this.deletedTextTarget.classList.remove('d-none')
      input.classList.add('d-none')
      this.element.classList.add('marked-for-deletion')
      this.updateGridCell(this.calculateTotal())
    }
  }

  restore = async () => {
    const input = this.unitInputTarget
    const item = this.buildItem(this.originalUnitCountValue, input)

    const success = await this.createTempServiceRecord(item)

    if (success) {
      this.deletedTextTarget.classList.add('d-none')
      input.classList.remove('d-none')
      this.element.classList.remove('marked-for-deletion')
      input.value = this.originalUnitCountValue
      this.updateGridCell(this.calculateTotal())
    }
  }

  calculateTotal = () => {
    // Find all inputs in the modal (across all rows)
    const allInputs = document.querySelectorAll('[data-grids--multiple-records-modal-row-target="unitInput"]')
    return Array.from(allInputs).reduce((total, input) => {
      const row = input.closest('[data-controller~="grids--multiple-records-modal-row"]')
      if (row && !row.classList.contains('marked-for-deletion')) {
        return total + (parseFloat(input.value) || 0)
      }
      return total
    }, 0)
  }

  updateGridCell = newTotal => {
    window.dispatchEvent(
      new CustomEvent(GRID_EVENTS.updateCellValue, {
        detail: {
          row_node_id: this.rowNodeIdValue,
          column_field: this.columnFieldValue,
          new_total: newTotal
        }
      })
    )
  }

  buildItem = unitCount => {
    return {
      customer_id: this.hasCustomerIdValue ? this.customerIdValue : null,
      client_care_relationship_id: this.hasClientCareRelationshipIdValue ? this.clientCareRelationshipIdValue : null,
      employee_id: this.hasEmployeeIdValue ? this.employeeIdValue : null,
      program_id: this.programIdValue,
      service_funding_source_id: this.serviceFundingSourceIdValue,
      provider_id: this.providerIdValue,
      location_provider_id: this.hasLocationProviderIdValue ? this.locationProviderIdValue : null,
      service_definition_id: this.serviceDefinitionIdValue,
      service_delivered_on: this.serviceDeliveredOnValue,
      service_record_id: this.hasServiceRecordIdValue ? this.serviceRecordIdValue : null,
      unit_count: unitCount,
      last_client_updated_at: new Date().toISOString()
    }
  }

  addRow = event => {
    event.preventDefault()

    const item = this.buildItem(0)
    const params = new URLSearchParams()

    params.append('upload_id', this.uploadIdValue)
    params.append('row_node_id', this.rowNodeIdValue)
    params.append('column_field', this.columnFieldValue)

    Object.keys(item).forEach(key => {
      if (item[key] != null) {
        params.append(key, item[key])
      }
    })

    const url = `${this.valuesUrlValue}/submissions/${this.uploadIdValue}/temp_service_records/create_new_row?${params}`

    turboRequest(url)
  }

  createTempServiceRecord = async item => {
    window.dispatchEvent(new CustomEvent(GRID_EVENTS.modalOperationStarted))

    const headers = {
      'Content-Type': 'application/json'
    }

    const csrfToken = document.querySelector('[name="csrf-token"]')
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken.content
    }

    try {
      const response = await fetch(
        `${this.valuesUrlValue}/submissions/${this.uploadIdValue}/temp_service_records/batch_create`,
        {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({
            service_record_grids_temp_service_records: [item]
          })
        }
      )

      const data = await response.json()

      if (data.success) {
        window.dispatchEvent(new CustomEvent(GRID_EVENTS.modalOperationCompleted))
        return true
      } else {
        window.dispatchEvent(new CustomEvent(GRID_EVENTS.modalOperationCompleted))
        document.dispatchEvent(
          new CustomEvent('toast', {
            detail: { failure: true, message: data['error_messages'] }
          })
        )
        return false
      }
    } catch (error) {
      window.dispatchEvent(new CustomEvent(GRID_EVENTS.modalOperationCompleted))
      console.error('Error updating temp service record:', error)
      document.dispatchEvent(
        new CustomEvent('toast', {
          detail: { failure: true, message: 'Failed to update service record' }
        })
      )
      return false
    }
  }
}
